import React, { useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { StyleSheet, ImageBackground } from 'react-native';

import { BrandColor } from '@styleseat/brand';
import {
  Box,
  Inline,
  BoxSizeOptions,
} from '@styleseat/ui';
import {
  Stack,
  Absolute,
  BodyText,
  Touchable,
  HeaderText,
  TextAlignOptions,
} from '..';

import { Video } from './Video';

import { VideoPlayIcon } from '../Icons/VideoPlayIcon';

import analytics from '../../../modules/analytics';

import type { MarketingVideo as MarketingVideoType } from '../../../store/MarketingVideos/MarketingVideos.model';

import './MarketingVideo.module.scss';

const PADDING_BOTTOM = 16;

const renderVideoDescriptionLink = video => {
  const { text, url } = video.videoDescriptionLink;
  const descriptionParts = video.videoDescription.split(text);
  return (
    <React.Fragment>
      {descriptionParts[0]}
      <a href={url}>{text}</a>
      {descriptionParts[1]}
    </React.Fragment>
  );
};

export const MarketingVideo = ({
  video,
  width,
  textAlign = 'center',
  textPadding = 16,
  videoPadding = 16,
  videoBorderRadius = 8,
  videoBottomPadding = PADDING_BOTTOM,
  videoPlayIcon,
}: {
  width: string;
  textAlign?: TextAlignOptions;
  textPadding?: BoxSizeOptions;
  videoBorderRadius?: number;
  videoPadding?: BoxSizeOptions;
  videoBottomPadding?: BoxSizeOptions;
  video: MarketingVideoType;
  videoPlayIcon?: any;
}) => {
  const videoRef = React.useRef<HTMLVideoElement>();
  const [showPlayText, setShowPlayText] = React.useState<boolean>(true);
  const [hasTracked, setHasTracked] = useState(false);

  const videoId = video.id || video.videoTitle;

  const visibilityChangeHandler = isVisible => {
    if (hasTracked) {
      return;
    }

    if (isVisible) {
      setHasTracked(true);
      analytics.track('marketing_video_visibile', { videoId });
    }
  };

  const icon = videoPlayIcon || (
    <React.Fragment>
      <Box
        color={BrandColor.Metal}
        minWidth={48}
        minHeight={48}
        borderRadius={50}
        alignItems="center"
        justifyContent="center"
      >
        <Box paddingLeft={4}>
          <VideoPlayIcon />
        </Box>
      </Box>
      <BodyText
        color={BrandColor.WhiteWash}
        appearance="large"
      >
        Play Now
      </BodyText>
    </React.Fragment>
  );

  const handleVideoClick = () => {
    if (videoRef.current.paused || videoRef.current.played.length === 0) {
      videoRef.current.play();
      // This is delayed to allow the feedback to show up on the screen.
      setTimeout(() => {
        setShowPlayText(false);
      }, 250);
      analytics.track('click', {
        component: 'MarketingVideo',
        effect: 'played',
        video_name: videoId,
      });
    } else {
      videoRef.current.pause();
      setShowPlayText(true);
      analytics.track('click', {
        component: 'MarketingVideo',
        effect: 'paused',
        video_name: videoId,
      });
    }
  };

  return (
    <Stack
      padding={videoPadding}
      maxWidth={width}
      alignItems="center"
      testID="marketing-video"
    >
      <Box
        paddingBottom={videoBottomPadding}
        minHeight={150}
        minWidth="100%"
      >
        <VisibilitySensor onChange={visibilityChangeHandler}>
          <Video
            ref={videoRef}
            onClick={handleVideoClick}
            sources={video.videoSources}
            borderRadius={videoBorderRadius}
          />
        </VisibilitySensor>

        <Absolute
          top={0}
          left={0}
          right={0}
          borderRadius={videoBorderRadius}
          overflow="hidden"
          testID="marketing_video-play_area"
          bottom={videoBottomPadding || PADDING_BOTTOM}
          style={{
            display: showPlayText ? null : 'none',
          }}
        >
          <ImageBackground
            source={{ uri: video.videoThumbnail }}
            style={StyleSheet.absoluteFill}
            accessibilityLabel={video.thumbnailDescription}
          >
            <Touchable onClick={handleVideoClick}>
              <Inline
                space={16}
                minHeight="100%"
                styleName="veil"
                alignItems="center"
                justifyContent="center"
              >
                {icon}
              </Inline>
            </Touchable>
          </ImageBackground>
        </Absolute>
      </Box>
      <Box
        paddingLeft={textPadding}
        paddingRight={textPadding}
        paddingBottom={textPadding}
      >
        <HeaderText level={3} textAlign={textAlign}>
          {video.videoTitle}
        </HeaderText>
        <BodyText
          color={BrandColor.Metal}
          textAlign={textAlign}
        >
          {
            'videoDescriptionLink' in video
              ? renderVideoDescriptionLink(video)
              : video.videoDescription
          }
        </BodyText>
      </Box>
    </Stack>
  );
};
